import { createMenuTool } from "./createMenuTool"
import type { MenuNode } from "@/ui/EditorJsMenu"

type Insertable = { toolName: string; label?: string; data?: any }
type DGCTConfig = {
  registeredToolKeys?: string[]
  createPortal: any
  removePortal: any
  insertables: {
    irrSeriesChart?: Insertable
    topTenPortfolioCompaniesTable?: Insertable
    topFiveSeriesTable?: Insertable
    geographicExposureTable?: Insertable
    sectorExposureTable?: Insertable
    usMap?: Insertable
    portfolioCompanyLogos?: Insertable
    subSectorPieChart?: Insertable
    historicalPnLByVintageTable?: Insertable
    historicalPnLByAssetClassTable?: Insertable
    historicalIrrByAssetClassTable?: Insertable
    historicalIrrByVintageTable?: Insertable
    historicalPnLBySeriesTable?: Insertable
    historicalIrrBySeriesTable?: Insertable
    performanceTables?: {
      label?: string
      bySeries: Insertable
      byStage: Insertable
      byAssetClass: Insertable
      byVintage: Insertable
      byLpEntity: Insertable
    }
  }
}

export const DataGeneratedContentTool = createMenuTool<DGCTConfig>({
  title: "Data generated content",
  buildMenu: ({ config, isAvailable }) => {
    const nodes: MenuNode[] = []

    // IRR chart (show only if enabled/registered)
    const irr = config.insertables.irrSeriesChart
    if (irr) {
      nodes.push({
        type: "action",
        icon: "chartLine",
        label: irr.label || "IRRSeriesChartTool",
        toolName: irr.toolName,
        data: irr.data,
        visible: isAvailable(irr.toolName),
      })
    }

    // Top 10 Portfolio Companies table
    const topTen = config.insertables.topTenPortfolioCompaniesTable
    if (topTen) {
      nodes.push({
        type: "action",
        icon: "sheet",
        label: topTen.label || "Top 10 Portfolio Companies",
        toolName: topTen.toolName,
        data: topTen.data,
        visible: isAvailable(topTen.toolName),
      })
    }

    // Top 5 Series table
    const topFive = config.insertables.topFiveSeriesTable
    if (topFive) {
      nodes.push({
        type: "action",
        icon: "sheet",
        label: topFive.label || "Top 5 Series",
        toolName: topFive.toolName,
        data: topFive.data,
        visible: isAvailable(topFive.toolName),
      })
    }

    // Historical IRR submenu
    const historicalIrrChildren: MenuNode[] = []

    const historicalIrrByAssetClass =
      config.insertables.historicalIrrByAssetClassTable
    if (
      historicalIrrByAssetClass &&
      isAvailable(historicalIrrByAssetClass.toolName)
    ) {
      historicalIrrChildren.push({
        type: "action",
        icon: "sheet",
        label: historicalIrrByAssetClass.label || "By Asset Class",
        toolName: historicalIrrByAssetClass.toolName,
        data: historicalIrrByAssetClass.data,
      })
    }

    const historicalIrrByVintage =
      config.insertables.historicalIrrByVintageTable
    if (
      historicalIrrByVintage &&
      isAvailable(historicalIrrByVintage.toolName)
    ) {
      historicalIrrChildren.push({
        type: "action",
        icon: "sheet",
        label: historicalIrrByVintage.label || "By Vintage",
        toolName: historicalIrrByVintage.toolName,
        data: historicalIrrByVintage.data,
      })
    }

    const historicalIrrBySeries = config.insertables.historicalIrrBySeriesTable
    if (historicalIrrBySeries && isAvailable(historicalIrrBySeries.toolName)) {
      historicalIrrChildren.push({
        type: "action",
        icon: "sheet",
        label: historicalIrrBySeries.label || "By Series",
        toolName: historicalIrrBySeries.toolName,
        data: historicalIrrBySeries.data,
      })
    }

    if (historicalIrrChildren.length > 0) {
      nodes.push({
        type: "submenu",
        icon: "trending",
        label: "Historical IRR",
        children: historicalIrrChildren,
      })
    }

    // Historical PnL submenu
    const historicalPnLChildren: MenuNode[] = []

    const historicalPnLByAssetClass =
      config.insertables.historicalPnLByAssetClassTable
    if (
      historicalPnLByAssetClass &&
      isAvailable(historicalPnLByAssetClass.toolName)
    ) {
      historicalPnLChildren.push({
        type: "action",
        icon: "sheet",
        label: historicalPnLByAssetClass.label || "By Asset Class",
        toolName: historicalPnLByAssetClass.toolName,
        data: historicalPnLByAssetClass.data,
      })
    }

    const historicalPnLByVintage =
      config.insertables.historicalPnLByVintageTable
    if (
      historicalPnLByVintage &&
      isAvailable(historicalPnLByVintage.toolName)
    ) {
      historicalPnLChildren.push({
        type: "action",
        icon: "sheet",
        label: historicalPnLByVintage.label || "By Vintage",
        toolName: historicalPnLByVintage.toolName,
        data: historicalPnLByVintage.data,
      })
    }

    const historicalPnLBySeries = config.insertables.historicalPnLBySeriesTable
    if (historicalPnLBySeries && isAvailable(historicalPnLBySeries.toolName)) {
      historicalPnLChildren.push({
        type: "action",
        icon: "sheet",
        label: historicalPnLBySeries.label || "By Series",
        toolName: historicalPnLBySeries.toolName,
        data: historicalPnLBySeries.data,
      })
    }

    if (historicalPnLChildren.length > 0) {
      nodes.push({
        type: "submenu",
        icon: "trending",
        label: "Historical P&L",
        children: historicalPnLChildren,
      })
    }

    // Exposure submenu
    const exposureChildren: MenuNode[] = []

    const geoExposure = config.insertables.geographicExposureTable
    if (geoExposure && isAvailable(geoExposure.toolName)) {
      exposureChildren.push({
        type: "action",
        icon: "sheet",
        label: geoExposure.label || "Geographic Exposure Table",
        toolName: geoExposure.toolName,
        data: geoExposure.data,
      })
    }

    const usMap = config.insertables.usMap
    if (usMap && isAvailable(usMap.toolName)) {
      exposureChildren.push({
        type: "action",
        icon: "globe",
        label: usMap.label || "Geographic Exposure Map",
        toolName: usMap.toolName,
        data: usMap.data,
      })
    }

    const sectorExposure = config.insertables.sectorExposureTable
    if (sectorExposure && isAvailable(sectorExposure.toolName)) {
      exposureChildren.push({
        type: "action",
        icon: "sheet",
        label: sectorExposure.label || "Sector Exposure Table",
        toolName: sectorExposure.toolName,
        data: sectorExposure.data,
      })
    }

    const pieChart = config.insertables.subSectorPieChart
    if (pieChart && isAvailable(pieChart.toolName)) {
      exposureChildren.push({
        type: "action",
        icon: "pieChart",
        label: pieChart.label || "Sub-Sector Pie Chart",
        toolName: pieChart.toolName,
        data: pieChart.data,
      })
    }

    if (exposureChildren.length > 0) {
      nodes.push({
        type: "submenu",
        icon: "globe",
        label: "Exposure",
        children: exposureChildren,
      })
    }

    // Portfolio Company Logos
    const logos = config.insertables.portfolioCompanyLogos
    if (logos) {
      nodes.push({
        type: "action",
        icon: "building",
        label: logos.label || "Portfolio Company Logos",
        toolName: logos.toolName,
        data: logos.data,
        visible: isAvailable(logos.toolName),
      })
    }

    // Performance tables submenu, only if at least one child is available
    const pt = config.insertables.performanceTables
    if (pt) {
      const children: MenuNode[] = []
      if (isAvailable(pt.bySeries.toolName)) {
        children.push({
          type: "action",
          icon: "sheet",
          label: pt.bySeries.label || "By series",
          toolName: pt.bySeries.toolName,
          data: pt.bySeries.data,
        })
      }
      if (isAvailable(pt.byStage.toolName)) {
        children.push({
          type: "action",
          icon: "sheet",
          label: pt.byStage.label || "By stage",
          toolName: pt.byStage.toolName,
          data: pt.byStage.data,
        })
      }
      if (isAvailable(pt.byAssetClass.toolName)) {
        children.push({
          type: "action",
          icon: "sheet",
          label: pt.byAssetClass.label || "By asset class",
          toolName: pt.byAssetClass.toolName,
          data: pt.byAssetClass.data,
        })
      }
      if (isAvailable(pt.byVintage.toolName)) {
        children.push({
          type: "action",
          icon: "sheet",
          label: pt.byVintage.label || "By vintage",
          toolName: pt.byVintage.toolName,
          data: pt.byVintage.data,
        })
      }
      if (isAvailable(pt.byLpEntity.toolName)) {
        children.push({
          type: "action",
          icon: "sheet",
          label: pt.byLpEntity.label || "By LP Entity",
          toolName: pt.byLpEntity.toolName,
          data: pt.byLpEntity.data,
        })
      }
      nodes.push({
        type: "submenu",
        label: pt.label || "Performance tables",
        children,
        visible: children.length > 0,
      })
    }

    return nodes
  },
})
